import React from 'react';
import { Box, Button, capitalize, FormControlLabel, Stack, Switch, Tooltip, Typography } from '@mui/material';

import {
  EditedCeilingPlaneProperties,
  EditedRoofPlaneProperties,
  InsertionCeilingPlane,
  InsertionRoofPlane,
} from '@paragon/layout';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { isInsertionPlaneFullyDefined } from '../../../../../store/slices/applicationSlice';
import { layoutSlice } from '../../../../../store/slices/layoutSlice';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { ProjectHierarchyContextSelects } from '../../properties/ProjectHierarchyContextSelects';
import { PlanePropertyControls } from '../../properties/roof-planes/PlanePropertyControls';
import InsertionPanelContainer from '../InsertionPanelContainer';

interface PropsForPlaneType<PlaneType, InsertionPlane, EditedProperties> {
  readonly type: PlaneType;

  readonly insertionPlane: InsertionPlane;
  readonly onChange: (changes: EditedProperties) => void;

  readonly onInsert: () => Promise<void>;
  readonly onInsertAndRepeat: () => Promise<void>;
}

export function PlaneInsertionSubPanel(
  props:
    | PropsForPlaneType<'roof', InsertionRoofPlane, EditedRoofPlaneProperties>
    | PropsForPlaneType<'ceiling', InsertionCeilingPlane, EditedCeilingPlaneProperties>,
) {
  const dispatch = useAppDispatch();

  const autoCutOnInsert = useAppSelector((state) => state.layout.roofPlaneInsertionSettings.autoCutOnInsert);

  const disabled = !isInsertionPlaneFullyDefined(props.insertionPlane);

  return (
    <InsertionPanelContainer
      disabled={disabled}
      onInsert={props.onInsert}
      additionalButtons={
        <Button variant="outlined" sx={{ m: 1, py: 1 }} disabled={disabled} onClick={props.onInsertAndRepeat}>
          Insert and Repeat
        </Button>
      }
    >
      <Box sx={{ p: 1, mb: 2 }} className="clickable-during-picking">
        <Typography align="center" variant="h5" sx={{ marginBottom: 3 }}>
          {capitalize(props.type)} Plane Creation
        </Typography>

        <Stack spacing={2}>
          <ProjectHierarchyContextSelects
            levelGuid={props.insertionPlane.levelGuid}
            onChange={(levelGuid) => props.onChange({ levelGuid })}
          />

          {props.type === 'roof' ? (
            <PlanePropertyControls type={props.type} properties={props.insertionPlane} onChange={props.onChange} />
          ) : props.type === 'ceiling' ? (
            <PlanePropertyControls type={props.type} properties={props.insertionPlane} onChange={props.onChange} />
          ) : (
            assertExhaustiveSwitch(props)
          )}

          {props.type === 'roof' && (
            <Tooltip disableInteractive title="Auto-cut added roof planes when done inserting">
              <FormControlLabel
                sx={{ ml: 1, mr: 1 }}
                label="Auto-Cut"
                control={
                  <Switch
                    checked={autoCutOnInsert}
                    onChange={(event) =>
                      dispatch(layoutSlice.actions.roofPlaneInsertionAutoCutSet(event.target.checked))
                    }
                  />
                }
              />
            </Tooltip>
          )}
        </Stack>
      </Box>
    </InsertionPanelContainer>
  );
}
